import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchAllDetailsCount,
  fetchPatientsPL,
  fetchAllVisitTypes,
  fetchAllStates,
  fetchFilteredVisitStages,
  fetchQueriesPL,
  fetchQueryFilteredVisitStages,
} from "../apis/patientService";
import {
  IVisitStage,
  IVisitType,
  IState,
  IStageCount,
} from "../interfaces/IPatient";
import { fetchAllSchemes } from "../apis/schemeService";
import { IScheme } from "../interfaces/IScheme";
import { IQueryFilter, IQueryState } from "../interfaces/IPreAuthQuery";
import { fetchQueriesNephro } from "../apis/nephroPlusService/OHDRService";

const initialState: IQueryState = {
  totalAvailableQueries: 0,
  queries: {},
  isFetchingVisitStages: false,
  isFetchingVisitTypes: false,
  isFetchingSchemes: false,
  isFetchingStates: false,
  isFetchingQueryById: false,
  isFetchingAllQueries: false,
  isFetchingQueryDetails: false,
  error: null,
  queryFilters: {
    endDate: "",
    orderBy: "DESC",
    pageNumber: 1,
    pageSize: 100,
    searchTerm: null,
    startDate: "",
    state: null,
    scheme: null,
    visitType: null,
    status: null,
    stage: null,
    selectedDateType: "DOA",
  },
  retainedQueryFilter: {
    selectedDatePreset: "lastWeek",
  },
  pageToQueryIdMap: {},
  isLoading: false,
  visitStages: {},
  visitTypes: {},
  schemes: {},
  states: {},
  stagesCount: {},
  allDetailsCount: {},
  visitStageId: null,
};

export const getQueriesPL = createAsyncThunk(
  "queries/getQueriesPL",
  async ({
    filters,
    healthCareId,
  }: {
    filters: IQueryFilter;
    healthCareId: number;
  }) => {
    // handling 0 base indexing on server
    filters = { ...filters, pageNumber: filters.pageNumber - 1 };
    const response = await fetchQueriesPL(filters, healthCareId);
    return response;
  }
);

export const getQueriesNephro = createAsyncThunk(
  "queries/getQueriesNephro",
  async ({
    filters,
    healthCareId,
  }: {
    filters: IQueryFilter;
    healthCareId: number;
  }) => {
    filters = { ...filters, pageNumber: filters.pageNumber - 1 };
    const response = await fetchQueriesNephro(filters, healthCareId);
    return response
  }
);

export const getAllVisitStages = createAsyncThunk(
  "queries/getAllVisitStages",
  async () => {
    const response = await fetchQueryFilteredVisitStages();
    return response;
  }
);

export const getAllSchemes = createAsyncThunk(
  "queries/getAllSchemes",
  async () => {
    const response = await fetchAllSchemes();
    return response;
  }
);

export const getAllStates = createAsyncThunk(
  "queries/getAllStates",
  async () => {
    const response = await fetchAllStates();
    return response;
  }
);

export const getAllVisitTypes = createAsyncThunk(
  "queries/getAllVisitTypes",
  async () => {
    const response = await fetchAllVisitTypes();
    return response;
  }
);

const querySlice = createSlice({
  name: "queries",
  initialState,
  reducers: {
    setQueryFilters: (state, action: PayloadAction<IQueryFilter>) => {
      state.queryFilters = action.payload;
    },
    setRetainedQueryFilters: (state, action) => {
      state.retainedQueryFilter = action.payload;
    },
    clearPageToQueryIdMap: (state) => {
      state.pageToQueryIdMap = {};
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(getAllVisitStages.pending, (state, action) => {
        state.isFetchingVisitStages = true;
        state.error = null;
      })
      .addCase(getAllVisitStages.fulfilled, (state, action: any) => {
        state.isFetchingVisitStages = false;
        if (action.payload) {
          action.payload?.forEach((visitStage: IVisitStage, idx: number) => {
            if (visitStage?.id) {
              state.visitStages[visitStage?.stageLevelId] = visitStage;
            }
          });
        }
        state.error =
          action.payload.errorMessage || "Failed to fetch all visit stages";
      })
      .addCase(getAllVisitStages.rejected, (state, action) => {
        state.isFetchingVisitStages = false;
        state.error =
          action.error.message || "Failed to fetch all visit stages";
      })
      .addCase(getAllStates.pending, (state) => {
        state.isFetchingStates = true;
        state.error = null;
      })
      .addCase(getAllStates.fulfilled, (state, action: any) => {
        state.isFetchingStates = false;
        if (action.payload) {
          action.payload?.forEach((stateItem: IState, idx: number) => {
            if (stateItem?.id) {
              state.states[stateItem?.id] = stateItem;
            }
          });
        }
        state.error = action.payload.errorMessage || "Failed to fetch states";
      })
      .addCase(getAllStates.rejected, (state, action) => {
        state.isFetchingStates = false;
        state.error = action.error.message || "Failed to fetch all states";
      })
      .addCase(getAllSchemes.pending, (state) => {
        state.isFetchingSchemes = true;
        state.error = null;
      })
      .addCase(getAllSchemes.fulfilled, (state, action: any) => {
        state.isFetchingSchemes = false;
        if (action.payload.schemes) {
          action.payload.schemes?.forEach((scheme: IScheme, idx: number) => {
            if (scheme?.id) {
              state.schemes[scheme?.id] = scheme;
            }
          });
        }
        state.error = action.payload.errorMessage || "Failed to fetch schemes";
      })
      .addCase(getAllSchemes.rejected, (state, action) => {
        state.isFetchingSchemes = false;
        state.error = action.error.message || "Failed to fetch all schemes";
      })
      .addCase(getAllVisitTypes.pending, (state, action) => {
        state.isFetchingVisitTypes = true;
        state.error = null;
      })
      .addCase(getAllVisitTypes.fulfilled, (state, action: any) => {
        state.isFetchingVisitTypes = false;
        if (action.payload) {
          action.payload?.forEach((visitType: IVisitType, idx: number) => {
            if (visitType?.id) {
              state.visitTypes[visitType?.id] = visitType;
            }
          });
        }
        state.error =
          action.payload.errorMessage || "Failed to fetch all visit types";
      })
      .addCase(getAllVisitTypes.rejected, (state, action) => {
        state.isFetchingVisitTypes = false;
        state.error = action.error.message || "Failed to fetch all visit types";
      })
      .addCase(getQueriesPL.pending, (state) => {
        state.queries = {};
        state.pageToQueryIdMap = {};
        state.isFetchingAllQueries = true;
        state.allDetailsCount = {};
        state.error = null;
      })
      .addCase(getQueriesPL.fulfilled, (state, action: any) => {
        state.isFetchingAllQueries = false;
        action.payload.stagesCount?.forEach((stageCount: IStageCount) => {
          Object.entries(stageCount)?.forEach(([stageName, count]) => {
            state.stagesCount[stageName.toUpperCase()] = {
              [stageName.toUpperCase()]: count,
            };
          });
        });

        state.queries = {};
        state.pageToQueryIdMap = {};
        state.allDetailsCount = {};

        action.payload.queryResponseList?.forEach((query: any) => {
          if (query?.queryId) {
            state.queries[query?.queryId] = query;

            if (!state.pageToQueryIdMap[state.queryFilters.pageNumber]) {
              state.pageToQueryIdMap[state.queryFilters.pageNumber] = [];
            }
            if (
              !state.pageToQueryIdMap[state.queryFilters.pageNumber].includes(
                query?.queryId
              )
            ) {
              state.pageToQueryIdMap[state.queryFilters.pageNumber].push(
                query?.queryId
              );
            }
          }
        });

        state.totalAvailableQueries = action.payload.totalElements || 0;
        state.queryFilters.pageSize = action.meta.arg.filters.pageSize;
        state.allDetailsCount = action.payload.overallQueryCount?.[0];
        state.visitStageId = action.payload.visitStageId;
        state.error = null;
      })
      .addCase(getQueriesPL.rejected, (state, action) => {
        state.isFetchingAllQueries = false;
        state.error = action.error.message || "Failed to fetch queries";
      })
      .addCase(getQueriesNephro.pending, (state) => {
        state.queries = {};
        state.pageToQueryIdMap = {};
        state.isFetchingAllQueries = true;
        state.allDetailsCount = {};
        state.error = null;
      })
      .addCase(getQueriesNephro.fulfilled, (state, action: any) => {
        state.isFetchingAllQueries = false;
        action.payload.stagesCount?.forEach((stageCount: IStageCount) => {
          Object.entries(stageCount)?.forEach(([stageName, count]) => {
            state.stagesCount[stageName.toUpperCase()] = {
              [stageName.toUpperCase()]: count,
            };
          });
        });

        state.queries = {};
        state.pageToQueryIdMap = {};
        state.allDetailsCount = {};

        action.payload.queryResponseList?.forEach((query: any) => {
          if (query?.queryId) {
            state.queries[query?.queryId] = query;

            if (!state.pageToQueryIdMap[state.queryFilters.pageNumber]) {
              state.pageToQueryIdMap[state.queryFilters.pageNumber] = [];
            }
            if (
              !state.pageToQueryIdMap[state.queryFilters.pageNumber].includes(
                query?.queryId
              )
            ) {
              state.pageToQueryIdMap[state.queryFilters.pageNumber].push(
                query?.queryId
              );
            }
          }
        });

        state.totalAvailableQueries = action.payload.totalElements || 0;
        state.queryFilters.pageSize = action.meta.arg.filters.pageSize;
        state.allDetailsCount = action.payload.overallQueryCount?.[0];
        state.visitStageId = action.payload.visitStageId;
        state.error = null;
      })
      .addCase(getQueriesNephro.rejected, (state, action) => {
        state.isFetchingAllQueries = false;
        state.error = action.error.message || "Failed to fetch queries";
      });
  },
});

export const {
  setQueryFilters,
  setRetainedQueryFilters,
  clearPageToQueryIdMap,
} = querySlice.actions;
export default querySlice.reducer;
